import {
  X,
  User,
  Clock,
  Globe,
  Monitor,
  FileText,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { AuditLog, auditService } from '../../../services/audit.service';
import { cn } from '../../../lib/utils';

interface AuditLogDetailProps {
  log: AuditLog;
  onClose: () => void;
}

export const AuditLogDetail: React.FC<AuditLogDetailProps> = ({ log, onClose }) => {
  const actionColor = auditService.getActionColor(log.action);
  const statusColor = auditService.getStatusColor(log.status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Audit Log Details
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              ID: {log.id}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Summary */}
          <div className="flex items-center gap-4">
            <span className={cn('px-3 py-1 text-sm font-medium rounded-full', actionColor)}>
              {auditService.getActionLabel(log.action)}
            </span>
            <span className={cn('inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full', statusColor)}>
              {log.status === 'success' ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
              {log.status}
            </span>
          </div>

          {/* Actor */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <User className="h-4 w-4" />
              Actor
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Name:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {log.userName || '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Email:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {log.userEmail || '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Role:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {log.userRole || '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Type:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {log.actorType}
                </span>
              </div>
            </div>
          </div>

          {/* Resource */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Resource
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Type:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {log.resource}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">ID:</span>
                <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">
                  {log.resourceId || '-'}
                </span>
              </div>
              {log.resourceName && (
                <div className="col-span-2">
                  <span className="text-gray-500 dark:text-gray-400">Name:</span>
                  <span className="ml-2 text-gray-900 dark:text-white">
                    {log.resourceName}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Changes */}
          {(log.oldValue || log.newValue) && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Changes
              </h3>
              {log.changedFields && log.changedFields.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-1">
                  {log.changedFields.map((field) => (
                    <span
                      key={field}
                      className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded dark:bg-blue-900/30 dark:text-blue-300"
                    >
                      {field}
                    </span>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                {log.oldValue && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Before
                    </h4>
                    <pre className="text-xs bg-red-50 dark:bg-red-900/20 text-gray-900 dark:text-gray-100 p-3 rounded overflow-auto max-h-48">
                      {JSON.stringify(log.oldValue, null, 2)}
                    </pre>
                  </div>
                )}
                {log.newValue && (
                  <div>
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      After
                    </h4>
                    <pre className="text-xs bg-green-50 dark:bg-green-900/20 text-gray-900 dark:text-gray-100 p-3 rounded overflow-auto max-h-48">
                      {JSON.stringify(log.newValue, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Request Context */}
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Request Context
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-500 dark:text-gray-400">Timestamp:</span>
                <span className="text-gray-900 dark:text-white">
                  {new Date(log.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-500 dark:text-gray-400">IP Address:</span>
                <span className="text-gray-900 dark:text-white font-mono">
                  {log.ipAddress || '-'}
                </span>
              </div>
              {log.userAgent && (
                <div className="flex items-start gap-2">
                  <Monitor className="h-4 w-4 text-gray-400 mt-0.5 dark:text-gray-500" />
                  <span className="text-gray-500 dark:text-gray-400">User Agent:</span>
                  <span className="text-gray-900 dark:text-white text-xs break-all">
                    {log.userAgent}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {log.errorMessage && (
            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
              <h3 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                Error Message
              </h3>
              <p className="text-sm text-red-600 dark:text-red-400">
                {log.errorMessage}
              </p>
            </div>
          )}

          {/* Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Additional Metadata
              </h3>
              <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-3 rounded overflow-auto max-h-48">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}

          {/* Tags */}
          {log.tags && log.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tags
              </h3>
              <div className="flex flex-wrap gap-1">
                {log.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded dark:bg-gray-700 dark:text-gray-300"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogDetail;
